import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  ContainerSection,
  MAutocomplete,
  MButton,
  MDatePicker,
  ResponsiveFormContainer,
} from "@app/components";
import {
  useAnalysisTypes,
  useEfficiencies,
  useEfficiencyParts,
  useEquipmentMide,
} from "@services/servicesGenericsTables";
import { IFilterCalibrationEficiency } from "@app/types/calibration.types";
import { MOCK_EFFICIENCY_TYPE } from "../../utils/constants";

const FilterCalibrationEfficiency = () => {
  const { analysisTypeList } = useAnalysisTypes();
  const { efficiencyPartsList } = useEfficiencyParts();
  const { setQuery } = useEfficiencies();

  const { fetchByAnalysisType, equipmentMideListFiltered, loading } =
    useEquipmentMide();

  const form = useForm<IFilterCalibrationEficiency>({ mode: "all" });

  const analysisWatch = form.watch("analysis");

  useEffect(() => {
    form.setValue("part", "");
    const fetchData = async () => {
      // Si hay análisis seleccionados, usar el primer ID para obtener equipos
      // En el futuro se podría mejorar para obtener equipos de todos los análisis
      const firstAnalysisId = Array.isArray(analysisWatch) && analysisWatch.length > 0 
        ? analysisWatch[0].id 
        : !Array.isArray(analysisWatch) ? analysisWatch?.id : undefined;
      
      if (firstAnalysisId) {
        await fetchByAnalysisType(firstAnalysisId as number);
      }
    };
    if (analysisWatch && (Array.isArray(analysisWatch) ? analysisWatch.length > 0 : true)) {
      fetchData();
    }
  }, [analysisWatch]);

  // Ordenar tipos de análisis alfabéticamente por descripción
  const sortedAnalysisTypeList = useMemo(() => {
    return [...analysisTypeList].sort((a, b) => 
      a.descripcion.localeCompare(b.descripcion, 'es', { sensitivity: 'base' })
    );
  }, [analysisTypeList]);

  const submit = (data: IFilterCalibrationEficiency) => {
    setQuery(data);
  };

  const clearFilters = () => {
    form.reset();
    setQuery({});
  };

  return (
    <ContainerSection label="Filtros">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submit)}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ResponsiveFormContainer columns={4}>
              <MAutocomplete
                key={JSON.stringify(form.getValues("efficiency_type"))}
                name="efficiency_type"
                options={MOCK_EFFICIENCY_TYPE}
                label="Seleccione una eficiencia"
                getOptionLabel={(option) => option.type || ""}
              />
              <MAutocomplete
                key={JSON.stringify(form.getValues("analysis"))}
                name="analysis"
                options={sortedAnalysisTypeList}
                label="Análisis"
                getOptionLabel={(option) => option.descripcion}
                multiple
              />
              <MAutocomplete
                key={JSON.stringify(form.getValues("equipment"))}
                name="equipment"
                options={equipmentMideListFiltered}
                label="Equipo/s"
                getOptionLabel={(option) =>
                  `${option.codigo} - ${option.descripcion}` || ""
                }
                multiple
                loading={analysisWatch && loading}
              />
              <MAutocomplete
                key={`${JSON.stringify(analysisWatch)}`}
                name="part"
                options={
                  analysisWatch && (Array.isArray(analysisWatch) ? analysisWatch.length > 0 : true)
                    ? efficiencyPartsList.filter((part) => {
                        if (Array.isArray(analysisWatch)) {
                          // Si hay múltiples análisis, incluir partes de cualquiera de ellos
                          return analysisWatch.some(analysis => part.analisis === analysis.codigo);
                        }
                        // Si es un solo análisis
                        return part.analisis === analysisWatch.codigo;
                      })
                    : []
                }
                label="Parte"
                getOptionLabel={(option) => option.parte || ""}
                helperText={!analysisWatch || (Array.isArray(analysisWatch) && analysisWatch.length === 0) ? "Seleccione un análisis" : ""}
                disabled={!analysisWatch || (Array.isArray(analysisWatch) && analysisWatch.length === 0)}
              />

              <MDatePicker
                name="date_from"
                pickerType="date"
                label="Fecha desde"
              />
              <MDatePicker
                name="date_to"
                pickerType="date"
                label="Fecha hasta"
              />
            </ResponsiveFormContainer>
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <MButton
                variant="outlined"
                color="primary"
                sx={{ textTransform: "uppercase" }}
                text="Filtrar"
                type="submit"
                startIcon={<FilterAltIcon />}
              />
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ textTransform: "uppercase", ml: 2 }}
                text="Limpiar"
                onClick={clearFilters}
              />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </ContainerSection>
  );
};

export default FilterCalibrationEfficiency;
